/**
 * Environment-based configuration for Sanity client.
 * This determines whether to show draft content (staging) or only published content (production).
 */

import { createClient } from '@sanity/client';
import { apiVersion, dataset, projectId } from './env';

export const isStaging = () => {
  return process.env.NEXT_PUBLIC_ENABLE_DRAFT_MODE === 'true';
};

export const getSanityConfig = () => {
  const isDraft = isStaging();
  return {
    token: isDraft ? process.env.SANITY_API_READ_TOKEN : undefined,
    perspective: isDraft ? ('drafts' as const) : ('published' as const),
  };
};

let cachedPerspective: string[] | undefined;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 60_000;

/**
 * Returns a perspective array that includes scheduled release names + 'drafts'.
 * In production, returns undefined (no override needed).
 * Results are cached for 60s to avoid repeated release queries.
 */
export const getStagingPerspective = async (): Promise<string[] | undefined> => {
  if (!isStaging()) {
    return undefined;
  }

  const now = Date.now();
  if (cachedPerspective && now - cacheTimestamp < CACHE_TTL_MS) {
    return cachedPerspective;
  }

  try {
    const tempClient = createClient({
      projectId,
      dataset,
      apiVersion,
      useCdn: false,
      token: process.env.SANITY_API_READ_TOKEN,
    });

    const releases = await tempClient.fetch<{ name: string }[]>(
      `releases::all()[state == "scheduled"]{ name }`,
    );

    const releaseNames = (releases || []).map((r) => r.name);
    cachedPerspective = [...releaseNames, 'drafts'];
    cacheTimestamp = now;

    return cachedPerspective;
  } catch (error) {
    // If releases query fails, fall back to just drafts
    cachedPerspective = ['drafts'];
    cacheTimestamp = now;
    return cachedPerspective;
  }
};
